import axios from 'axios';
import { RiskMapReadResponse, RisksPostResponse } from './RiskMapResponses';
import { RiskMapUpdateRequest } from './RiskMapRequests';
import { Risk } from '../../../riskMap/types/Risk';

// Create axios instance with credentials enabled
const apiClient = axios.create({
    withCredentials: true,
});

export class RiskMapRequest {
    private readonly baseUrl: string;
    
    constructor() {
        this.baseUrl = import.meta.env.VITE_API_BASE_URL;
    }

    /**
     * Fetches risk map data by ID
     * @param id - The ID of the risk map to fetch
     * @returns Promise with the risk map data (risks and residual risks)
     * @throws Error if the request fails
     */
    public async getRiskMap(id: string): Promise<RiskMapReadResponse> {
        try {
            const response = await apiClient.get<RiskMapReadResponse>(
                `${this.baseUrl}/riskMaps/${id}`
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to fetch risk map: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Creates a new risk map
     * @param riskMapData - The risk map data to create
     * @returns Promise with the created risk map data
     * @throws Error if the request fails
     */
    public async createRiskMap(): Promise<RiskMapReadResponse> {
        try {
            const response = await apiClient.post<RiskMapReadResponse>(
                `${this.baseUrl}/riskMaps`,
                {}
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to create risk map: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Updates an existing risk map
     * @param id - The ID of the risk map to update
     * @param riskMapData - The updated risk map data
     * @returns Promise with the updated risk map data
     * @throws Error if the request fails
     */
    public async updateRiskMap(id: string, riskMapData: RiskMapUpdateRequest): Promise<RiskMapReadResponse> {
        try {
            const response = await apiClient.put<RiskMapReadResponse>(
                `${this.baseUrl}/riskMaps/${id}`,
                riskMapData
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to update risk map: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Deletes a risk map
     * @param id - The ID of the risk map to delete
     * @returns Promise that resolves when the risk map is deleted
     * @throws Error if the request fails
     */
    public async deleteRiskMap(id: string): Promise<void> {
        try {
            await apiClient.delete(`${this.baseUrl}/riskMaps/${id}`);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to delete risk map: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Gets all risks for a specific risk map
     * @param riskMapId - The ID of the risk map
     * @returns Promise with the array of risks
     * @throws Error if the request fails
     */
    public async getRisks(riskMapId: string): Promise<Risk[]> {
        try {
            const response = await apiClient.get<Risk[]>(
                `${this.baseUrl}/riskMaps/${riskMapId}/risks`
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to fetch risks: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Creates a new risk in a specific risk map
     * @param riskMapId - The ID of the risk map
     * @param risk - The risk data to create
     * @returns Promise with the created risk
     * @throws Error if the request fails
     */
    public async postRisk(riskMapId: string, risk: Omit<Risk, 'id'>): Promise<Risk> {
        try {
            const response = await apiClient.post<Risk>(
                `${this.baseUrl}/riskMaps/${riskMapId}/risks`,
                risk
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to create risk: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Updates an existing risk in a specific risk map
     * @param riskMapId - The ID of the risk map
     * @param riskId - The ID of the risk to update
     * @param risk - The updated risk data
     * @returns Promise with the updated risk
     * @throws Error if the request fails
     */
    public async updateRisk(riskMapId: string, riskId: string, risk: Risk): Promise<Risk> {
        try {
            const response = await apiClient.put<Risk>(
                `${this.baseUrl}/riskMaps/${riskMapId}/risks/${riskId}`,
                risk
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to update risk: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Deletes a risk from a specific risk map
     * @param riskMapId - The ID of the risk map
     * @param riskId - The ID of the risk to delete
     * @returns Promise that resolves when the risk is deleted
     * @throws Error if the request fails
     */
    public async deleteRisk(riskMapId: string, riskId: string): Promise<void> {
        try {
            await apiClient.delete(`${this.baseUrl}/riskMaps/${riskMapId}/risks/${riskId}`);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to delete risk: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Gets all risk maps
     * @returns Promise with the array of risk maps
     * @throws Error if the request fails
     */
    public async getAllRiskMaps(): Promise<RiskMapReadResponse[]> {
        try {
            const response = await apiClient.get<RiskMapReadResponse[]>(
                `${this.baseUrl}/riskMaps`
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Failed to fetch risk maps: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Posts risks to a risk map
     * @param riskMapId - The ID of the risk map
     * @param risks - Array of risks to post
     * @returns Promise that resolves when the risks are posted
     * @throws Error if the request fails
     */
    public async postRisks(riskMapId: string, body: Omit<Risk, 'id'>[]): Promise<RisksPostResponse> {
        try {
            const response = await apiClient.post<RisksPostResponse>(
                `${this.baseUrl}/riskMaps/${riskMapId}/risks/bulk`,
                body
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const msg = error.response?.data?.message ?? error.response?.data?.detail ?? error.message;
                const status = error.response?.status;
                throw new Error(status === 400 ? `Invalid import data: ${msg}` : `Failed to import risks: ${msg}`);
            }
            throw error;
        }
    }
}

// Create a singleton instance
export const riskMapRequest = new RiskMapRequest();
